<template>
  <div class="expense-mobile">
    <!-- Filter summary bar -->
    <expense-filter-row 
      @click="filterVisible = true"
      :filter="filter"
      :count="rows.length"
      :total="total"
    ></expense-filter-row>

    <!-- Action buttons -->
    <div class="container-fluid px-0 my-2">
      <div class="row g-2">
        <div class="col-4">
          <button @click="refresh()"
            class="btn btn-primary form-control"
          >
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="col-4">
          <button @click="newRecord()"
            class="btn btn-success form-control"
          >
            <i class="fa fa-plus"></i> New
          </button>
        </div>
        <div class="col-4">
          <button @click="filterVisible = true"
            class="btn btn-outline-secondary form-control"
          >
            <i class="fa fa-filter"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <hr class="my-2" />

    <!-- Mobile-optimized list view -->
    <div class="mobile-expense-list">
      <div v-for="row in rows" :key="row.Id" class="expense-card mb-2" @click="editRecord(row.Id)">
        <div class="card">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="flex-grow-1">
                <strong>{{ row.Description || 'No Description' }}</strong>
                <div class="text-muted small">{{ row.Contact?.Company || 'N/A' }}</div>
              </div>
              <div class="text-end ms-2">
                <span class="badge bg-success">
                  ${{ (row.Amount || 0).toFixed(2) }}
                </span>
              </div>
            </div>
            
            <div class="small mb-1" v-if="row.Request?.Id">
              <i class="fas fa-ticket-alt me-1"></i>Request #{{ row.Request.Id }}
            </div>
            
            <div class="small mb-1" v-if="row.Technician?.TechnicianName">
              <i class="fas fa-user me-1"></i>{{ row.Technician.TechnicianName }}
            </div>
            
            <div class="small text-truncate" v-if="row.Vendor">
              <i class="fas fa-store me-1"></i>{{ row.Vendor }}
            </div>
            
            <div class="small text-truncate" v-if="row.ExpenseType">
              <i class="fas fa-tag me-1"></i>{{ row.ExpenseType }}
            </div>
            
            <div class="small text-muted" v-if="row.Date">
              <i class="fas fa-calendar me-1"></i>{{ getLocalDate(row.Date) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="rows.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-receipt fa-3x mb-3"></i>
        <p>No expenses found</p>
      </div>
    </div>

    <!-- Filter Modal -->
    <kt-modal 
      :visible="filterVisible" 
      title="Filter Expenses" 
      @dismiss="filterVisible = false"
    >
      <template #body>
        <expense-filter
          :filter="filter" 
          @dismiss="filterVisible = false" 
          @update="setFilter($event)"
        ></expense-filter>
      </template>
    </kt-modal>
  </div>
</template>

<style scoped>
.expense-mobile {
  padding: 0.5rem;
}

.expense-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.expense-card:active {
  transform: scale(0.98);
}

.expense-card .card {
  border-left: 3px solid #198754;
}

.mobile-expense-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>

<script setup lang="ts">
import { ref } from "vue";
import Helper from "@/services/Helper";
import KtModal from "@/components/UI/KtModal.vue";
import ExpenseFilterRow from "@/components/Expenses/ExpenseFilterRow.vue";
import ExpenseFilter from "@/components/Expenses/ExpenseFilter.vue";

// Props - receive data from parent
const props = defineProps<{
  rows: any[];
  total: number;
  filter: any;
}>();

// Emits
const emit = defineEmits<{
  refresh: [];
  newRecord: [];
  editRecord: [id: number];
  setFilter: [filter: any];
}>();

const filterVisible = ref(false);

// Methods
const refresh = () => {
  emit('refresh');
};

const newRecord = () => {
  emit('newRecord');
};

const editRecord = (id: number) => {
  emit('editRecord', id);
};

const setFilter = (filter: any) => {
  emit('setFilter', filter);
  filterVisible.value = false;
};

const getLocalDate = (val: any) => {
  if (!val) return '';
  let tmp = new Date(val);
  return Helper.dateToInput(tmp, Helper.DateFormat_Date);
};
</script>
